import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { OrganizationSetupForm } from '../OrganizationSetupForm';
import { useAuth } from '../../contexts/AuthContext';
import ProfileModal from './ProfileModal';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { AnalyticsService } from '../../services/AnalyticsService';
import { useLanguage } from '../../contexts/LanguageContext';
import { JoinClubRequestForm } from '../membership/JoinClubRequestForm';
import { Input } from '../ui/input';

function Guidance({ step }: { step: string }) {
  const { t } = useLanguage();
  switch (step) {
    case 'initial':
      return <div className="flex items-center gap-2 mb-4 text-gray-600"><InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" /><span>{t('onboarding.guidance.initial')}</span></div>;
    case 'create':
      return <div className="flex items-center gap-2 mb-4 text-gray-600"><InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" /><span>{t('onboarding.guidance.create')}</span></div>;
    case 'join':
      return <div className="flex items-center gap-2 mb-4 text-gray-600"><InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" /><span>{t('onboarding.guidance.join')}</span></div>;
    case 'profile':
      return <div className="flex items-center gap-2 mb-4 text-gray-600"><InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" /><span>{t('onboarding.guidance.profile')}</span></div>;
    case 'complete':
      return <div className="flex items-center gap-2 mb-4 text-gray-600"><InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" /><span>{t('onboarding.guidance.complete')}</span></div>;
    default:
      return null;
  }
}

function NeedHelp() {
  const { t } = useLanguage();
  return (
    <div className="mt-8 text-center">
      <a href="mailto:hello@omniflyer.com" className="text-blue-600 hover:underline text-sm">{t('onboarding.needHelp')}</a>
    </div>
  );
}

export const InitialModal: React.FC = () => {
  const { t } = useLanguage();
  const { currentStep, setCurrentStep } = useOnboarding();
  const { logout } = useAuth();
  React.useEffect(() => { AnalyticsService.logEvent('onboarding_step_initial'); }, []);
  return (
    <Dialog
      open={currentStep === 'initial'}
      onClose={() => {}}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-2xl bg-white p-8 shadow-2xl">
          <Dialog.Title className="text-2xl font-bold text-gray-900 mb-4 text-center">
            {t('onboarding.welcome')}
          </Dialog.Title>
          <Guidance step="initial" />
          <div className="space-y-4 mt-6">
            <button
              onClick={() => { setCurrentStep('create'); AnalyticsService.logEvent('onboarding_chose_create_org'); }}
              className="w-full px-4 py-3 text-base font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('onboarding.createNewOrganization')}
            </button>
            <button
              onClick={() => { setCurrentStep('join'); AnalyticsService.logEvent('onboarding_chose_join_org'); }}
              className="w-full px-4 py-3 text-base font-semibold text-blue-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('onboarding.joinExistingOrganization')}
            </button>
          </div>
          <div className="border-t border-gray-200 my-6" />
          <button
            onClick={logout}
            className="w-full px-4 py-2 text-sm font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Logout
          </button>
          <NeedHelp />
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export const JoinModal: React.FC = () => {
  const { t } = useLanguage();
  const { currentStep, handleJoinOrganization, previousStep } = useOnboarding();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [organizationData, setOrganizationData] = useState<any>(null);

  React.useEffect(() => { if (currentStep === 'join') AnalyticsService.logEvent('onboarding_step_join'); }, [currentStep]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await handleJoinOrganization(code);
      // If result is an object, it means it's a club requiring approval
      if (result && typeof result === 'object' && result.requiresApproval) {
        setOrganizationData(result);
        setShowRequestForm(true);
        setLoading(false);
      } else {
        // Instant join for non-clubs
        AnalyticsService.logEvent('onboarding_joined_org');
      }
    } catch (err) {
      setError(t('onboarding.invalidInvitationCode'));
      AnalyticsService.logEvent('onboarding_error_invalid_code');
      setLoading(false);
    }
  };

  const handleRequestSuccess = (id: string) => {
    // Deliberately leave showRequestForm/organizationData alone here — unmounting
    // JoinClubRequestForm at this point would skip its own success screen (with
    // the "View Request Status" link) before it ever gets to render. The form
    // stays open and shows that screen itself; its own onClose resets this state.
    AnalyticsService.logEvent('membership_request_submitted', { requestId: id });
  };

  // Show request form for clubs
  if (showRequestForm && organizationData) {
    return (
      <JoinClubRequestForm
        isOpen={currentStep === 'join'}
        onClose={() => {
          setShowRequestForm(false);
          setOrganizationData(null);
          setCode('');
        }}
        onSuccess={handleRequestSuccess}
        organization={{
          id: organizationData.orgId,
          name: organizationData.organizationName,
          description: organizationData.organizationDescription,
          baseAirport: organizationData.baseAirport,
          type: organizationData.organizationType as 'club' | 'school' | 'company',
          joinCode: organizationData.joinCode,
          ownerId: '',
          createdAt: new Date(),
          updatedAt: new Date(),
        }}
        joinCode={code}
      />
    );
  }

  // Show join code input form
  return (
    <Dialog
      open={currentStep === 'join'}
      onClose={() => {}}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4">
            {t('onboarding.joinOrganization')}
          </Dialog.Title>
          <Guidance step="join" />
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="text"
                id="code"
                label={t('onboarding.invitationCode')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('onboarding.enterInvitationCode')}
                required
              />
            </div>
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={() => {
                  setCode('');
                  setError(null);
                  setLoading(false);
                  previousStep();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('onboarding.joining') : t('onboarding.joinOrganization')}
              </button>
            </div>
          </form>
          <NeedHelp />
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export const CreateOrganizationModal: React.FC = () => {
  const { currentStep, handleCreateOrganization, previousStep } = useOnboarding();
  const { currentUser } = useAuth();
  React.useEffect(() => { if (currentStep === 'create') AnalyticsService.logEvent('onboarding_step_create'); }, [currentStep]);
  if (!currentUser) return null;
  return (
    <OrganizationSetupForm
      isOpen={currentStep === 'create'}
      ownerUid={currentUser.uid}
      onSuccess={(org) => { handleCreateOrganization(org); AnalyticsService.logEvent('onboarding_created_org', { orgId: org.id }); }}
      onCancel={previousStep}
      guidance={<Guidance step="create" />}
      stepper={<div className="flex items-center justify-center mb-6 w-full max-w-xs mx-auto">
        {/* Removed stepper */}
      </div>}
      needHelp={<NeedHelp />}
    />
  );
};

export { ProfileModal };

export const OnboardingModals: React.FC = () => {
  const { t } = useLanguage();
  const { currentStep } = useOnboarding();
  const validSteps = ['initial', 'create', 'join', 'profile', 'complete'];
  return (
    <>
      { !validSteps.includes(currentStep) ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center p-8">
            <h2 className="text-2xl font-bold text-red-600 mb-2">{t('onboarding.error')}</h2>
            <p className="text-gray-700 mb-4">{t('onboarding.errorMessage')}</p>
            <a href="mailto:hello@omniflyer.com" className="text-blue-600 hover:underline text-sm">{t('onboarding.contactSupport')}</a>
          </div>
        </div>
      ) : (
        <>
          <InitialModal />
          <JoinModal />
          <CreateOrganizationModal />
          <ProfileModal />
        </>
      )}
    </>
  );
}; 